import {
  faLockKeyhole,
  faMagnifyingGlass,
  faMobile,
  faStopwatch,
  faWallet,
} from '@fortawesome/pro-light-svg-icons'
import Typography from '@mui/material/Typography'
import Link from 'next/link'
import { useAnalytics } from '../../context/Analytics'
import { ValueProp } from './components'

const zeroLiabilityUrl =
  'https://usa.visa.com/pay-with-visa/visa-chip-technology-consumers/zero-liability-policy.html'

interface ProductPageSecondaryBenefitsSectionProps {
  variant?: 'default' | 'light'
}

const ProductPageSecondaryBenefitsSection = ({
  variant = 'default',
}: ProductPageSecondaryBenefitsSectionProps) => {
  const { track, events } = useAnalytics()

  const valuePropItems = [
    {
      icon: faLockKeyhole,
      iconMargin: '-12px',
      title: (
        <>
          Visa<sup>®</sup> Zero Liability Policy
        </>
      ),
      description: (
        <>
          <span>
            You&apos;re protected against unauthorized transactions if your card is ever lost or
            stolen.
          </span>{' '}
          <Link
            href={zeroLiabilityUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block md:inline"
            onClick={() =>
              track?.(events.INTERACTION.ClickCta, {
                cta: 'Visa Zero Liability - Learn More',
              })
            }
          >
            Learn more.
          </Link>
        </>
      ),
    },
    {
      icon: faStopwatch,
      iconMargin: '-10px',
      title: 'Automatic payment reminders',
      description:
        'Never miss a due date again with helpful payment reminders that keep your credit on track.',
    },
    {
      icon: faMagnifyingGlass,
      iconMargin: '-6px',
      title: 'Free online credit score',
      description:
        'Stay on top of your credit with free access to your TransUnion score in the Mission Lane app.',
    },
    {
      icon: faWallet,
      iconMargin: '-6px',
      title: 'Digital wallet',
      description: (
        <>
          Add your card to Apple Pay<sup>®</sup>, Google Pay, Samsung Pay, and more for easy and
          secure payments wherever you go.
        </>
      ),
    },
    {
      icon: faMobile,
      iconMargin: '-12px',
      title: 'Mission Lane mobile app',
      description:
        'Check your balance, pay your bills, and review your transactions on the go, anytime, anywhere.',
    },
  ]

  return (
    <div
      className={`py-16 md:py-24 text-center md:text-left ${
        variant === 'default' ? 'bg-ink-200' : ''
      }`}
    >
      <div className="grid grid-cols-12 mx-auto px-4 md:px-8 max-w-[1440px]" data-nosnippet>
        <Typography variant="h2" className="col-span-full pb-6 md:pb-16">
          Simple and secure, <br className="hidden md:inline" />
          with benefits built for you
        </Typography>
        <div className="col-span-full grid grid-cols-3 gap-x-6 gap-y-6 md:gap-y-12">
          {valuePropItems.map((valuePropItem, idx) => {
            let i = idx
            if (idx > 2) i = idx - 3

            return (
              <div
                key={`value-prop-${idx}`}
                className={`col-span-full md:col-span-1 md:col-start-${i + 1}`}
              >
                <ValueProp {...valuePropItem} />
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default ProductPageSecondaryBenefitsSection
